import { useState } from "react";
import { Download, Eye } from "lucide-react";
import clsx from "clsx";

import { Card } from "../../../../../../shared/card/card.tsx";
import { LinearProgress } from "../../../../../../ui/linear-progress/linear-progress.tsx";
import { Button } from "../../../../../../ui/button/button.tsx";
import { ProjectOverview } from "../../../types.ts";
import { ViewTimesheetsModal } from "../../modals/view-timesheets-modal/view-timesheets-modal.tsx";
import { DownloadTimesheetsModal } from "../../modals/download-timesheets-modal/download-timesheets-modal.tsx";

import styles from "./project-allocation-cards.module.css";

interface ProjectAllocationCardsProps {
  projects: ProjectOverview[];
  employeeId?: string;
  /** View/Download actions — only on Time Allocated Overview, not the timesheet Overview tab. */
  showActions?: boolean;
}

export const ProjectAllocationCards = ({
  projects,
  employeeId,
  showActions = false,
}: ProjectAllocationCardsProps) => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  return (
    <>
      <Card padding="5xl" className={styles.container}>
        <div className={styles.headerRow}>
          <div className={styles.headerText}>
            <h3 className="heading-sm">Project Allocation Overview</h3>
            <p className="sub-heading-sm">
              Allocated vs Actual vs Remaining hours
            </p>
          </div>
          {showActions ? (
            <div className={styles.headerActions}>
              <Button
                variant="accent-outline"
                size="sm"
                startIcon={<Eye size={14} aria-hidden />}
                onClick={() => setIsViewOpen(true)}
              >
                View Timesheet
              </Button>
              <Button
                variant="accent-outline"
                size="sm"
                startIcon={<Download size={14} aria-hidden />}
                onClick={() => setIsDownloadOpen(true)}
              >
                Download Timesheet
              </Button>
            </div>
          ) : null}
        </div>

        <div className={styles.cardsList}>
          {projects.map((project) => {
            const isOverUtilized = project.utilization > 100;
            const status = isOverUtilized ? "warning" : "success";

            const formatTimeString = (timeStr: string | undefined | null) => {
              if (!timeStr) return "0";
              const cleanStr = timeStr.replace("-", "");
              const parts = cleanStr.split(":");
              if (parts.length < 2) return cleanStr;
              const [h, m] = parts.map(Number);
              const total = h + m / 60;
              return Number(total.toFixed(1)).toString();
            };

            const formatValue = (val: number | undefined | null) =>
              val !== undefined && val !== null
                ? Number(val.toFixed(1)).toString()
                : "0";

            return (
              <Card key={project.projectId} variant="default" padding="5xl">
                <div className={styles.cardTopRow}>
                  <div className={styles.projectInfo}>
                    <h4 className="heading-xs">{project.projectName || ""}</h4>
                  </div>
                  <div className={styles.utilizationBlock}>
                    <div
                      className={clsx("heading-xl", styles.utilizationPercent)}
                    >
                      {formatValue(project.utilization)}%
                    </div>
                    <div className="sub-heading-xs">Utilization</div>
                  </div>
                </div>

                <LinearProgress
                  value={Math.min(project.utilization, 100)}
                  variant={status === "warning" ? "accent" : "success"}
                  className={styles.linearProgress}
                />

                <div className={styles.statsGrid}>
                  <div className={styles.statCol}>
                    <span className="sub-heading-sm">Allocated Hours</span>
                    <span className={clsx("heading-sm", styles.statValueBlack)}>
                      {formatTimeString(project.allocatedTime)}
                    </span>
                  </div>
                  <div className={styles.statCol}>
                    <span className="sub-heading-sm">Actual Hours</span>
                    <span className={clsx("heading-sm", styles.statValueTeal)}>
                      {formatTimeString(project.actualTime)}
                    </span>
                  </div>
                  <div className={styles.statCol}>
                    <span className="sub-heading-sm">Remaining Hours</span>
                    <span
                      className={clsx(
                        "heading-sm",
                        status === "success" && styles.statValueGreen,
                        status === "warning" && styles.statValueAmber,
                      )}
                    >
                      {formatTimeString(project.remainingTime)}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
          {projects.length === 0 ? (
            <div className={styles.emptyState}>
              No project data found for the current period.
            </div>
          ) : null}
        </div>
      </Card>

      {showActions ? (
        <>
          <ViewTimesheetsModal
            open={isViewOpen}
            onClose={() => setIsViewOpen(false)}
            employeeId={employeeId}
          />
          <DownloadTimesheetsModal
            open={isDownloadOpen}
            onClose={() => setIsDownloadOpen(false)}
            employeeId={employeeId}
          />
        </>
      ) : null}
    </>
  );
};
